import { cn } from '@/lib/utils'
import LinkWithTransition from '@/v2/components/helpers/link-with-transition'
import ResponsiveImage from '@/v2/components/helpers/responsive-image'
import SectionHead from '@/v2/components/sections/landing-pages/section-head'
import { Button } from '@/v2/components/ui/button'
import SectionWrapper from '@/v2/components/wrappers/section-wrapper'
import { CardType } from '@/v2/types/cards'
import { domAnimation, LazyMotion, m } from 'framer-motion'
import React from 'react'

interface ServiceTipsSectionSectionProps {
  title: string
  description?: string
  cards: CardType[]
  className?: string
  ctaButton?: { href: string; text: string }
  isBgDark?: boolean
  imgClass?: string
  sectionTitleClassName?: string
}

const ServiceTipsSectionSection: React.FC<ServiceTipsSectionSectionProps> = props => {
  const { title, description, cards, className, ctaButton, isBgDark = false, sectionTitleClassName, imgClass } = props

  return (
    <LazyMotion features={domAnimation}>
      <m.section className={cn('landing-pages-wr section-spacing', isBgDark ? 'bg-[#F9F8F8]' : '', className)}>
        <SectionWrapper>
          <SectionHead title={title} description={description} className={sectionTitleClassName} />

          <div className='grid lg-mn:grid-cols-3 md-mn:grid-cols-2 grid-cols-1 md-mn:gap-5 gap-3'>
            {cards.map((card, index) => (
              <m.article
                key={card.id}
                className='flex items-start flex-col py-5 px-3 [&>h3]:text-paragraph
                [&>h3]:font-semibold xl-min:[&>h3]:text-xl [&>h3]:w-full [&>h3]:text-lg [&>h3]:mb-3 [&>h3]:mt-5 transition-all md-mn:text-left text-center'
                initial='hidden'
                whileInView='visible'
                viewport={{ once: false, amount: 0.3 }}
                transition={{
                  delay: index * 0.1,
                  duration: 0.6
                }}
              >
                {card.image && (
                  <div className='md-mn:self-start self-center'>
                    <ResponsiveImage
                      src={card.image.src}
                      alt={card.image.alt}
                      className={cn('h-[130px] w-[130px]', imgClass)}
                    />
                  </div>
                )}
                <h3>{card.title}</h3>

                {card.description && (
                  <p className='text-paragraph leading-6 font-normal text-base'>{card.description}</p>
                )}
                {card.points && (
                  <ul className='list-outside list-disc ml-5'>
                    {card.points.map((point, pointIdx) => (
                      <li
                        key={pointIdx}
                        dangerouslySetInnerHTML={{ __html: point }}
                        className='text-paragraph font-normal leading-6 text-base break-words text-left'
                      />
                    ))}
                  </ul>
                )}
              </m.article>
            ))}
          </div>

          {ctaButton && (
            <LinkWithTransition href={ctaButton.href} className='mt-12 flex items-center justify-center'>
              <Button variant='call_to_action' size='lg' className='transition-all animate-bounce self-center'>
                {ctaButton.text}
              </Button>
            </LinkWithTransition>
          )}
        </SectionWrapper>
      </m.section>
    </LazyMotion>
  )
}

export default ServiceTipsSectionSection
